import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, Mail, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function AdminLogin() {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/admin" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back, admin");
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-[85vh] flex items-center justify-center px-6 py-24 overflow-hidden" data-testid="admin-login-page">
      <div className="aurora" />
      <div className="absolute inset-0 grid-bg opacity-70" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[500px] radial-glow-red pointer-events-none" />

      <form
        onSubmit={handleSubmit}
        className="relative z-10 w-full max-w-md glass rounded-3xl p-8 md:p-10 space-y-6"
        data-testid="admin-login-form"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-red-600/15 border border-red-500/40 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-red-400 font-mono-brand">Restricted</div>
            <div className="font-display text-2xl font-bold">Admin Access</div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-slate-300">Email</Label>
            <div className="relative">
              <Mail className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@beeasycars.com"
                className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-red-500"
                data-testid="admin-login-email"
                required
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-slate-300">Password</Label>
            <div className="relative">
              <Lock className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-red-500"
                data-testid="admin-login-password"
                required
              />
            </div>
          </div>
        </div>

        <Button
          type="submit"
          disabled={submitting}
          className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold rounded-full h-12"
          data-testid="admin-login-submit"
        >
          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Sign In
        </Button>

        <p className="text-xs text-slate-500 text-center">
          Be Easy Cars — Admin Panel. Unauthorized access is prohibited.
        </p>
      </form>
    </div>
  );
}
